import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Card from "../ui/Card";
import Table from "../ui/Table";
import { addSupportReply, fetchSupportTickets, resolveSupportTicket } from "../utils/support";
import { notifySupportRecipient } from "../utils/notifySupport";

export default function SupportInboxPage() {
  const { user, activeOrgId } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [filter, setFilter] = useState("open");
  const [reply, setReply] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [resolving, setResolving] = useState(false);

  const orgId = activeOrgId || user?.orgId || "org_default";

  const refresh = useCallback(async () => {
    const rows = await fetchSupportTickets(user, orgId, { status: filter, max: 50 });
    setTickets(rows || []);
    setActiveId((current) => (
      rows?.some((row) => row.id === current)
        ? current
        : (rows?.[0]?.id || "")
    ));
  }, [user, orgId, filter]);

  useEffect(() => {
    if (!user?.uid) return;
    refresh();
  }, [user, refresh]);

  const activeTicket = useMemo(
    () => tickets.find((t) => t.id === activeId) || null,
    [tickets, activeId]
  );

  const handleReply = async () => {
    if (!reply.trim() || !activeTicket) return;
    setStatusMsg("Sending reply...");
    const ok = await addSupportReply(user, orgId, activeTicket.id, reply.trim(), "superadmin");
    if (ok) {
      notifySupportRecipient(activeTicket.userEmail, {
        subject: `Support update • ${activeTicket.subject}`,
        message: reply.trim(),
      }).catch(() => {});
      setReply("");
      setStatusMsg("Reply sent.");
      refresh();
    } else {
      setStatusMsg("Unable to send reply.");
    }
    setTimeout(() => setStatusMsg(""), 3500);
  };

  const handleResolve = async () => {
    if (!activeTicket) return;
    if (String(activeTicket.status || "").toLowerCase() === "resolved") return;
    setStatusMsg("Resolving...");
    setResolving(true);
    const ok = await resolveSupportTicket(user, orgId, activeTicket.id, "Marked as resolved.");
    if (ok) {
      notifySupportRecipient(activeTicket.userEmail, {
        subject: `Support resolved • ${activeTicket.subject}`,
        message: "Your support request has been resolved. Reply if you need anything else.",
      }).catch(() => {});
      setStatusMsg("Resolved.");
      refresh();
    } else {
      setStatusMsg("Unable to resolve.");
    }
    setResolving(false);
    setTimeout(() => setStatusMsg(""), 3500);
  };

  const rows = tickets.map((t, i) => ([
    i + 1,
    t.userName || t.userEmail || "User",
    t.subject || "Support request",
    t.category || "General",
    (t.status || "open").toUpperCase(),
    t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "-",
  ]));

  return (
    <div className="nia-page">
      <Card className="nia-hero">
        <div className="nia-header">
          <div>
            <h2>Support Inbox</h2>
            <p>Track, respond to, and resolve user issues.</p>
          </div>
          <div className="support-actions">
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>
      </Card>

      <div className="nia-grid nia-grid--2 support-grid">
        <Card title="Tickets">
          <Table
            headers={["#", "User", "Subject", "Category", "Status", "Created"]}
            rows={rows.length ? rows : [["-", "-", "-", "-", "-", "No tickets."]]}
            onRowClick={(rowIndex) => {
              const t = tickets[rowIndex];
              if (t?.id) setActiveId(t.id);
            }}
          />
        </Card>

        <Card title="Conversation">
          {!activeTicket ? (
            <div className="nia-muted">Select a ticket to view details.</div>
          ) : (
            <>
              <div className="nia-list" style={{ marginBottom: 12 }}>
                <div className="nia-list-btn">
                  <span>{activeTicket.subject}</span>
                  <span className="nia-pill">{activeTicket.status || "open"}</span>
                </div>
                <div className="nia-muted">
                  {activeTicket.userName || activeTicket.userEmail} • {activeTicket.category || "General"}
                </div>
                {String(activeTicket.status || "").toLowerCase() !== "resolved" && (
                  <div style={{ marginTop: 10 }}>
                    <button className="btn btn-ghost" onClick={handleResolve} disabled={resolving}>
                      {resolving ? "Resolving..." : "Mark resolved"}
                    </button>
                  </div>
                )}
              </div>

              <div className="nia-modal-body" style={{ maxHeight: 240, overflow: "auto" }}>
                {(activeTicket.messages || []).map((m) => (
                  <div key={m.id || m.createdAt} style={{ marginBottom: 10 }}>
                    <div style={{ fontWeight: 600 }}>{m.authorRole || "user"}</div>
                    <div className="nia-muted" style={{ fontSize: 12 }}>
                      {m.createdAt ? new Date(m.createdAt).toLocaleString() : "Just now"}
                    </div>
                    <div>{m.message}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 12 }}>
                <label>Reply</label>
                <textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Write a response…"
                />
                <div className="support-reply-actions">
                  {String(activeTicket.status || "").toLowerCase() !== "resolved" && (
                    <button className="btn btn-ghost" onClick={handleResolve} disabled={resolving}>
                      {resolving ? "Resolving..." : "Mark resolved"}
                    </button>
                  )}
                  <button className="btn btn-primary" onClick={handleReply}>Send reply</button>
                </div>
                {statusMsg && <div className="nia-muted" style={{ marginTop: 8 }}>{statusMsg}</div>}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
